/* Width and Length are presentation controls; Dawn still owns the native Size and variant. */
(() => {
  if (customElements.get('ona-size-split')) return;
  const dimensions = value => {
    const match = String(value).match(/^\s*(\d+(?:\.\d+)?)\s*[″"]?\s*W\s*[×x]\s*(\d+(?:\.\d+)?)\s*[″"]?\s*L\s*$/i);
    return match ? { width: Number(match[1]), length: Number(match[2]) } : null;
  };
  // Open on the longest length unless an outside link (ad, share) names an exact variant.
  // Applied once per page load; Dawn re-renders must not undo a shopper's later choice.
  let longestPending = true;
  const preferLongest = () => {
    if (!new URLSearchParams(location.search).has('variant')) return true;
    try {
      const from = new URL(document.referrer);
      return from.origin === location.origin && !/^\/(cart|checkouts?)\b/.test(from.pathname);
    } catch { return false; }
  };
  class OnaSizeSplit extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.info = this.closest('product-info');
      if (!this.info) return;
      try { this.variants = JSON.parse(this.querySelector('[data-dimension-variants]').textContent); } catch { return; }
      this.sizeIndex = Number(this.dataset.sizeIndex);
      if (!this.variants.length || this.variants.some(row => !dimensions(row.options[this.sizeIndex]))) return;
      this.width = this.querySelector('[data-dimension="width"]');
      this.length = this.querySelector('[data-dimension="length"]');
      this.notice = this.querySelector('[role="status"]');
      const query = new URLSearchParams(location.search);
      const asked = { width: Number(query.get('width')), length: Number(query.get('length')) };
      this.requested = query.has('width') && Number.isFinite(asked.width) && asked.width > 0
        ? { width: asked.width, length: Number.isFinite(asked.length) && asked.length > 0 ? asked.length : 96 } : null;
      this.longestFirst = this.requested ? longestPending : longestPending && preferLongest();
      this.controller = new AbortController();
      const { signal } = this.controller;
      const schedule = () => {
        if (this.frame == null) this.frame = requestAnimationFrame(() => { this.frame = null; this.sync(); });
      };
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-dimension]')) return;
        event.stopPropagation();
        this.choose(event.target.dataset.dimension);
      }, { signal });
      this.info.addEventListener('change', event => { if (event.target.closest('variant-selects')) schedule(); }, { signal });
      this.observer = new MutationObserver(schedule);
      this.observer.observe(this.info, { childList: true, subtree: true, attributes: true, attributeFilter: ['selected', 'disabled'] });
      this.sync();
    }
    selection() {
      const picker = this.info.querySelector('variant-selects');
      this.native = picker?.querySelector('select[name="options[Size]"]');
      if (!this.native) return null;
      const values = [...picker.querySelectorAll('fieldset, .product-form__input--dropdown')].map(group => (group.querySelector('input:checked') || group.querySelector('select'))?.value);
      // On a partial colour × size matrix (velvet) ona-media.js hides and disables the
      // sizes that do not exist in the chosen colour. Those options are not candidates:
      // counting them makes sync() commit a size the gallery immediately filters out
      // again, and the two MutationObservers re-enter each other until the page stops
      // responding. Falls back to every option if nothing is marked selectable.
      const selectable = [...this.native.options].filter(option => !option.disabled && !option.hidden);
      const nativeValues = new Set((selectable.length ? selectable : [...this.native.options]).map(option => option.value));
      const rows = this.variants.filter(row => row.available && nativeValues.has(row.options[this.sizeIndex]) && row.options.every((value, index) => index === this.sizeIndex || value === values[index])).map(row => ({ value: row.options[this.sizeIndex], ...dimensions(row.options[this.sizeIndex]) }));
      return { rows, current: dimensions(this.native.value) };
    }
    renderOptions(select, values, value) {
      const unique = [...new Set(values)].sort((a,b) => a-b);
      const key = unique.join('|');
      if (select.dataset.values !== key) {
        select.replaceChildren(...unique.map(number => new Option(`${number}″`, String(number))));
        select.dataset.values = key;
      }
      select.value = String(value);
    }
    nearest(rows, width, length) {
      return [...rows].sort((a,b) => Math.abs(a.width-width)-Math.abs(b.width-width) || Math.abs(a.length-length)-Math.abs(b.length-length))[0];
    }
    sync() {
      if (!this.isConnected) return;
      const state = this.selection();
      if (!state?.rows.length || !state.current) {
        this.hidden = true; this.info.removeAttribute('data-ona-size-split'); return;
      }
      const { rows, current } = state;
      if (!this.longestFirst) longestPending = false;
      const row = rows.find(item => item.width === current.width && item.length === current.length) || this.nearest(rows, current.width, current.length);
      if (this.longestFirst) {
        this.longestFirst = false;
        longestPending = false;
        const longest = this.requested ? this.nearest(rows, this.requested.width, this.requested.length)
          : [...rows].sort((a,b) => b.length-a.length || a.width-b.width)[0];
        // Dawn must own the switch so price and the cart's variant id follow; the controls re-sync after it re-renders.
        if (longest.value !== row.value) {
          const apply = () => requestAnimationFrame(() => { if (this.isConnected && this.native?.value === row.value) this.commit(longest.value); });
          const ready = () => customElements.whenDefined('product-info').then(apply);
          document.readyState === 'complete' ? ready() : window.addEventListener('load', ready, { once: true, signal: this.controller.signal });
        }
      }
      this.renderOptions(this.width, rows.map(item => item.width), row.width);
      this.renderOptions(this.length, rows.filter(item => item.width === row.width).map(item => item.length), row.length);
      this.info.setAttribute('data-ona-size-split', '');
      this.hidden = false;
      if (this.native.value !== row.value) {
        const message = `Available size selected: ${row.width}″ wide × ${row.length}″ long.`;
        if (this.notice.textContent !== message) this.notice.textContent = message;
        this.commit(row.value);
      }
    }
    choose(dimension) {
      const state = this.selection();
      if (!state?.rows.length) return;
      const width = Number(this.width.value), length = Number(this.length.value);
      const row = this.nearest(state.rows.filter(item => item.width === width), width, length);
      if (!row) { this.sync(); return; }
      this.notice.textContent = dimension === 'width' && row.length !== length ? `Length updated to ${row.length}″ for this width.` : '';
      this.commit(row.value);
      this.sync();
    }
    commit(value) {
      if (this.native.value === value || ![...this.native.options].some(option => option.value === value)) return;
      this.native.value = value;
      this.native.dispatchEvent(new Event('change', { bubbles: true }));
    }
    disconnectedCallback() {
      this.controller?.abort(); this.observer?.disconnect();
      if (this.frame != null) cancelAnimationFrame(this.frame);
      this.info?.removeAttribute('data-ona-size-split');
      this.controller = null;
    }
  }
  customElements.define('ona-size-split', OnaSizeSplit);
})();
